import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { reportsAPI } from '../lib/api';
import { useProject } from '../lib/project-context';
import { RequiresProject } from '../components/RequiresContext';
import { useProjects, useScenarios, useReports, useGenerateReport, useDeleteReport, useSettings } from '../lib/queries';
import { formatCurrency, formatPercent, formatNumber } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  FileText, FileBarChart, Table2, Shield, BarChart3,
  Plus, Printer, Trash2, Eye, Loader2, Calendar, User, ChevronRight,
  Clock, Filter, ArrowRight, Download, RefreshCw
} from 'lucide-react';

const TYPES = {
  project_summary:    { icon: FileBarChart, color: '#6366F1', label: 'Resumen Financiero del Proyecto', desc: 'KPIs principales, ingresos, costos, utilidad, capital y alertas.', needsScenario: true },
  scenario_executive: { icon: FileText,     color: '#059669', label: 'Resumen Ejecutivo del Escenario',  desc: 'Diagnóstico de viabilidad, riesgos, oportunidades y recomendaciones.', needsScenario: true },
  corrida_financiera: { icon: Table2,       color: '#0B1220', label: 'Corrida Financiera',               desc: 'Tabla transposed bancaria: supuestos, ingresos, egresos y resumen.', needsScenario: true },
  monthly_cashflow:   { icon: BarChart3,    color: '#D97706', label: 'Flujo Mensual Detallado',          desc: 'Tabla mensual de ingresos, egresos, financiamiento y flujo neto.', needsScenario: true },
  charts_summary:     { icon: BarChart3,    color: '#6D28D9', label: 'Resumen de Gráficas',              desc: 'Datos tabulares de flujo, deuda y composición para análisis visual.', needsScenario: true },
  scenario_compare:   { icon: Table2,       color: '#6D28D9', label: 'Comparativo de Escenarios',        desc: 'Métricas lado a lado entre escenarios con deltas y conclusiones.', needsCompare: true },
  risk_alerts:        { icon: Shield,       color: '#DC2626', label: 'Riesgos y Alertas',                desc: 'Alertas priorizadas con severidad, explicación y recomendaciones.', needsScenario: true },
  full_detailed:      { icon: FileBarChart, color: '#6366F1', label: 'Reporte Detallado Completo',       desc: 'Reporte integral: KPIs, costos, flujo, corrida, riesgos y recomendaciones.', needsScenario: true },
};

/* ═══════════════════════════ Premium Report Renderers ═══════════════════════════ */

const S = {
  section: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#6366F1', margin: '0 0 14px 0', paddingBottom: '6px', borderBottom: '2px solid #6366F1' },
  subsection: { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#9CA3AF', margin: '24px 0 10px 0' },
  label: { fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.7px', color: '#9CA3AF', margin: '0 0 5px 0' },
  bigNum: { fontSize: '22px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', color: '#0B1220', margin: 0, lineHeight: 1.2 },
  medNum: { fontSize: '16px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', color: '#0B1220', margin: 0, lineHeight: 1.2 },
  body: { fontSize: '12px', color: '#4B5563', lineHeight: 1.65, margin: '0 0 6px 0' },
  muted: { fontSize: '11px', color: '#9CA3AF', margin: 0 },
  divider: { height: '1px', background: '#E5E7EB', margin: '20px 0' },
  card: { border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px' },
  cardHighlight: { border: '2px solid #6366F1', borderRadius: '10px', padding: '16px', background: '#F1F5FF' },
};

// SWA-167 · tratamiento visual por estado del dictamen. La etiqueta viene
// de verdict.label; el frontend no reconstruye el dictamen.
const VERDICT_STYLES = {
  viable: { border: '#A5D8B5', bg: '#F6FBF8', text: '#059669' },
  condicionado: { border: '#E5CFA5', bg: '#FFFBF5', text: '#D97706' },
  no_viable: { border: '#E5AAAA', bg: '#FDF2F2', text: '#DC2626' },
  no_concluyente: { border: '#E5E7EB', bg: '#F9FAFB', text: '#6B7280' },
};

function verdictView(verdict) {
  const state = verdict?.state;
  const sv = VERDICT_STYLES[state] || VERDICT_STYLES.no_concluyente;
  return { ...sv, label: verdict?.label || 'Retorno No Concluyente' };
}

function RenderKPIs({ metrics, highlight = [] }) {
  const items = [
    { key: 'irr_annual', label: 'TIR del inversionista', value: formatPercent(metrics?.irr_annual) },
    { key: 'mirr', label: 'MIRR del inversionista', value: formatPercent(metrics?.mirr) },
    { key: 'npv', label: 'VAN del inversionista', value: formatCurrency(metrics?.npv) },
    { key: 'roi', label: 'ROI', value: formatPercent(metrics?.roi) },
    { key: 'moic', label: 'MOIC del inversionista', value: metrics?.moic ? `${formatNumber(metrics.moic, 2)}x` : 'N/A' },
    { key: 'payback_month', label: 'Payback', value: metrics?.payback_month ? `Mes ${metrics.payback_month}` : 'N/A' },
    { key: 'peak_equity', label: 'Capital Máximo', value: formatCurrency(metrics?.peak_equity) },
    { key: 'peak_debt', label: 'Deuda Máxima', value: formatCurrency(metrics?.peak_debt) },
    { key: 'margen_neto', label: 'Margen Neto', value: formatPercent(metrics?.margen_neto) },
  ];
  // SWA-167 · si la TIR no es confiable, el destaque pasa a MIRR
  const hl = metrics?.is_irr_reliable !== true
    ? highlight.map((k) => (k === 'irr_annual' ? 'mirr' : k))
    : highlight;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '28px' }}>
      {items.map((item) => (
        <div key={item.key} style={hl.includes(item.key) ? S.cardHighlight : S.card}>
          <p style={S.label}>{item.label}</p>
          <p style={hl.includes(item.key) ? { ...S.bigNum, color: '#6366F1' } : S.medNum}>{item.value}</p>
        </div>
      ))}
    </div>
  );
}

function RenderAlertsList({ alerts, compact = false }) {
  if (!alerts?.length) return null;
  const sevStyle = { critical: { bg: '#FDF2F2', border: '#E5AAAA', dot: '#DC2626', text: '#7F1D1D' }, warning: { bg: '#FFFBF5', border: '#E5CFA5', dot: '#D97706', text: '#78350F' }, info: { bg: '#F0FAFB', border: '#B5DDE2', dot: '#6366F1', text: '#1E3A5F' } };
  return alerts.map((a, i) => {
    const sv = sevStyle[a.severity] || sevStyle.info;
    const rk = `alert-${a.severity}-${i}-${(a.title || '').slice(0, 24)}`;
    return compact ? (
      <div key={rk} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: sv.dot, marginTop: '5px', flexShrink: 0 }} />
        <div><span style={{ fontSize: '11px', fontWeight: 600, color: sv.text }}>{a.title}</span>{a.recommendation && <span style={{ fontSize: '11px', color: '#9CA3AF' }}> — {a.recommendation}</span>}</div>
      </div>
    ) : (
      <div key={rk} style={{ padding: '12px 16px', marginBottom: '8px', borderRadius: '10px', borderLeft: `4px solid ${sv.dot}`, background: sv.bg }}>
        <p style={{ fontSize: '12px', fontWeight: 700, color: sv.text, margin: '0 0 3px 0' }}>{a.title}</p>
        <p style={{ fontSize: '11px', color: '#4B5563', margin: '0 0 6px 0', lineHeight: 1.5 }}>{a.description}</p>
        {a.recommendation && <p style={{ fontSize: '11px', color: sv.text, margin: 0 }}><strong>Recomendación:</strong> {a.recommendation}</p>}
      </div>
    );
  });
}

const RENDERERS = {
  project_summary: ({ content: c }) => {
    const k = c?.kpis || {}; const s = c?.summary || {}; const cs = c?.cost_breakdown || {};
    return (<div>
      <p style={S.section}>Indicadores Clave</p>
      <RenderKPIs metrics={k} highlight={['irr_annual', 'npv']} />

      <p style={S.section}>Resumen Financiero</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '28px' }}>
        <div style={S.card}>
          <p style={S.label}>Ingresos Totales</p>
          <p style={{ ...S.bigNum, color: '#059669' }}>{formatCurrency(s.total_revenue)}</p>
          <p style={{ ...S.muted, marginTop: '6px' }}>{c?.revenue_summary?.total_units || 0} unidades | {formatCurrency(c?.revenue_summary?.avg_price_per_unit)} promedio</p>
        </div>
        <div style={S.card}>
          <p style={S.label}>Costo Total del Proyecto</p>
          <p style={{ ...S.bigNum, color: '#DC2626' }}>{formatCurrency(s.total_cost)}</p>
          <p style={{ ...S.muted, marginTop: '6px' }}>Terreno: {formatCurrency(cs.land_cost)} | Duros: {formatCurrency(cs.hard_costs)}</p>
        </div>
        <div style={S.card}>
          <p style={S.label}>Utilidad Neta Estimada</p>
          <p style={{ ...S.bigNum, color: s.total_profit >= 0 ? '#059669' : '#DC2626' }}>{formatCurrency(s.total_profit)}</p>
          <p style={{ ...S.muted, marginTop: '6px' }}>Intereses: {formatCurrency(s.total_interest)} | Equity: {formatCurrency(s.total_equity)}</p>
        </div>
      </div>

      {cs && (<>
        <p style={S.section}>Desglose de Costos</p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '28px' }}>
          <tbody>
            {[['Terreno', cs.land_cost],['Preoperativos', cs.preoperative_costs],['Costos de construcción', cs.hard_costs],['Costos blandos', cs.soft_costs],['Gastos indirectos', cs.indirect_costs],['Contingencia', cs.contingency],['Fee de desarrollo', cs.development_fee]].map(([label, val]) => (
              <tr key={label} style={{ borderBottom: '1px solid #F0EDE8' }}>
                <td style={{ padding: '8px 0', color: '#4B5563' }}>{label}</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontFamily: 'Plus Jakarta Sans, Inter, monospace', fontWeight: 600 }}>{formatCurrency(val)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #E5E7EB' }}>
              <td style={{ padding: '10px 0', fontWeight: 700 }}>Total</td>
              <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'Plus Jakarta Sans, Inter, monospace', fontWeight: 700, fontSize: '14px' }}>{formatCurrency(cs.total_project_cost)}</td>
            </tr>
          </tbody>
        </table>
      </>)}

      {(c?.alerts || []).length > 0 && (<>
        <p style={S.section}>Alertas de Riesgo</p>
        <RenderAlertsList alerts={c.alerts} />
      </>)}
    </div>);
  },

  scenario_executive: ({ content: c }) => {
    const k = c?.kpis || {};
    const v = verdictView(c?.verdict);
    return (<div>
      <div style={{ padding: '18px 20px', borderRadius: '12px', border: `2px solid ${v.border}`, background: v.bg, marginBottom: '28px' }}>
        <p style={{ fontSize: '16px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', margin: '0 0 6px 0' }}>
          Diagnóstico: <span style={{ color: v.text }}>{v.label}</span>
        </p>
        <p style={S.body}>{c?.diagnosis_text}</p>
      </div>

      <p style={S.section}>Métricas Clave</p>
      <RenderKPIs metrics={k} highlight={['irr_annual', 'npv']} />

      {c?.top_risk && (<>
        <p style={S.section}>Principal Riesgo</p>
        <div style={{ padding: '14px 18px', borderRadius: '10px', borderLeft: '4px solid #DC2626', background: '#FDF2F2', marginBottom: '24px' }}>
          <p style={{ fontSize: '13px', fontWeight: 700, color: '#7F1D1D', margin: '0 0 4px 0' }}>{c.top_risk.title}</p>
          <p style={{ ...S.body, color: '#4B5563' }}>{c.top_risk.description}</p>
          <p style={{ fontSize: '12px', fontWeight: 600, color: '#7F1D1D', margin: 0 }}>Recomendación: {c.top_risk.recommendation}</p>
        </div>
      </>)}

      {c?.top_opportunity && (<>
        <p style={S.section}>Oportunidad</p>
        <div style={{ padding: '14px 18px', borderRadius: '10px', borderLeft: '4px solid #059669', background: '#F6FBF8', marginBottom: '24px' }}>
          <p style={{ fontSize: '12px', color: '#4B5563', margin: 0, lineHeight: 1.6 }}>{c.top_opportunity}</p>
        </div>
      </>)}

      {c?.recommendations?.length > 0 && (<>
        <p style={S.section}>Recomendaciones</p>
        <div style={{ marginBottom: '24px' }}>
          {c.recommendations.map((r, i) => (
            <div key={`rec-${i}-${(r || '').slice(0, 24)}`} style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#6366F1', color: 'white', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
              <p style={{ ...S.body, margin: 0, paddingTop: '2px' }}>{r}</p>
            </div>
          ))}
        </div>
      </>)}

      {(c?.alerts || []).filter(a => a.severity !== 'info').length > 0 && (<>
        <p style={S.section}>Todas las Alertas</p>
        <RenderAlertsList alerts={c.alerts} compact />
      </>)}
    </div>);
  },

  scenario_compare: ({ content: c }) => {
    const scenarios = c?.scenarios || [];
    const metrics = [['irr_annual','TIR del inversionista','pct'],['npv','VAN del inversionista','cur'],['roi','ROI','pct'],['moic','MOIC del inversionista','x'],['total_profit','Utilidad Neta','cur'],['total_revenue','Ingreso Total','cur'],['total_cost','Costo Total','cur'],['peak_equity','Capital Máximo','cur'],['peak_debt','Deuda Máxima','cur'],['payback_month','Payback','mo'],['margen_neto','Margen Neto','pct']];
    const fmt = (v, f) => v == null ? '—' : f === 'pct' ? formatPercent(v) : f === 'cur' ? formatCurrency(v) : f === 'x' ? `${formatNumber(v,2)}x` : f === 'mo' ? `Mes ${v}` : String(v);
    return (<div>
      <p style={S.section}>Tabla Comparativa</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '28px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #6366F1' }}>
            <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.6px', color: '#9CA3AF' }}>Métrica</th>
            {scenarios.map(s => <th key={s.scenario_id} style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', fontSize: '13px' }}>{s.scenario_name}</th>)}
          </tr>
        </thead>
        <tbody>
          {metrics.map(([key, label, f], ri) => (
            <tr key={key} style={{ borderBottom: '1px solid #F0EDE8', background: ri % 2 === 0 ? '#FAFAF8' : 'white' }}>
              <td style={{ padding: '9px 12px', color: '#4B5563', fontWeight: 500 }}>{label}</td>
              {scenarios.map((s, si) => {
                const delta = si > 0 ? s.deltas_vs_base?.[key] : null;
                return (
                  <td key={s.scenario_id} style={{ textAlign: 'right', padding: '9px 12px', fontFamily: 'Plus Jakarta Sans, Inter, monospace', fontWeight: 600 }}>
                    {fmt(s.metrics?.[key], f)}
                    {delta != null && <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 600, color: delta >= 0 ? '#059669' : '#DC2626' }}>{delta >= 0 ? '+' : ''}{delta.toFixed(1)}%</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {scenarios.some(s => s.alerts_count > 0) && (<>
        <p style={S.section}>Alertas por Escenario</p>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${scenarios.length}, 1fr)`, gap: '12px' }}>
          {scenarios.map(s => (
            <div key={s.scenario_id} style={S.card}>
              <p style={{ ...S.label, marginBottom: '8px' }}>{s.scenario_name}</p>
              <p style={{ ...S.medNum, color: s.alerts_count > 0 ? '#D97706' : '#059669' }}>{s.alerts_count} {s.alerts_count === 1 ? 'alerta' : 'alertas'}</p>
            </div>
          ))}
        </div>
      </>)}
    </div>);
  },

  monthly_cashflow: ({ content: c }) => {
    // SWA-409 · solo pinta claves del backend (report_routes._generate_monthly_cashflow), sin aritmética local.
    // Total Ingresos − Total Egresos = Flujo Neto; la aportación de capital es fondeo y va fuera de la identidad.
    const rows = c?.rows || []; const t = c?.totals || {};
    const cols = [
      ['Cobranza', 'cobranza'], ['Crédito', 'credito'], ['Total Ingresos', 'total_ingresos'],
      ['Costos', 'costos'], ['Intereses', 'intereses'], ['Comisiones crédito', 'comisiones_credito'], ['Amortización', 'amortizacion'],
      ['Interés pasivos', 'interes_pasivos'], ['Capital pasivos', 'capital_pasivos'], ['Total Egresos', 'total_egresos'],
    ];
    const tail = [['Aportación de capital (fondeo)', 'aportacion_capital'], ['Flujo Acumulado', 'flujo_acumulado'], ['Saldo Crédito', 'saldo_credito']];
    const cell = { textAlign: 'right', padding: '6px 8px', fontFamily: 'monospace' };
    const pos = (v) => (v > 0 ? formatCurrency(v) : '—');
    const color = (v) => (v >= 0 ? '#059669' : '#DC2626');
    return (<div>
      <p style={S.section}>Flujo de Efectivo Mensual</p>
      <div style={{ overflow: 'auto', maxHeight: '520px', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
        <table className="mcf-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead style={{ position: 'sticky', top: 0 }}>
            <tr style={{ background: '#EEF2F7', borderBottom: '2px solid #E5E7EB' }}>
              {['Mes', ...cols.map(([h]) => h), 'Flujo Neto', ...tail.map(([h]) => h)].map(h => (
                <th key={h} style={{ textAlign: h === 'Mes' ? 'center' : 'right', padding: '8px 8px', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF', background: '#EEF2F7', fontWeight: 700 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.month} style={{ borderBottom: '1px solid #F0EDE8', background: i % 2 === 0 ? '#FAFAF8' : 'white' }}>
                <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>{r.month}</td>
                {cols.map(([, k]) => (
                  <td key={k} style={{ ...cell, fontWeight: k.startsWith('total') ? 600 : 400 }}>{pos(r[k])}</td>
                ))}
                <td style={{ ...cell, fontWeight: 700, color: color(r.flujo_neto) }}>{formatCurrency(r.flujo_neto)}</td>
                <td style={{ ...cell, color: '#6366F1' }}>{pos(r.aportacion_capital)}</td>
                <td style={{ ...cell, fontWeight: 700, color: color(r.flujo_acumulado) }}>{formatCurrency(r.flujo_acumulado)}</td>
                <td style={{ ...cell, color: '#9CA3AF' }}>{pos(r.saldo_credito)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #E5E7EB', background: '#EEF2F7' }}>
              <td style={{ textAlign: 'center', padding: '8px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>TOTAL</td>
              {cols.map(([, k]) => (
                <td key={k} style={{ ...cell, padding: '8px', fontWeight: 700 }}>{pos(t[k])}</td>
              ))}
              <td style={{ ...cell, padding: '8px', fontWeight: 700, color: color(t.flujo_neto) }}>{formatCurrency(t.flujo_neto)}</td>
              <td style={{ ...cell, padding: '8px', fontWeight: 700, color: '#6366F1' }}>{pos(t.aportacion_capital)}</td>
              <td style={{ ...cell, padding: '8px', fontWeight: 700, color: color(t.flujo_acumulado) }}>{formatCurrency(t.flujo_acumulado)}</td>
              <td style={{ ...cell, padding: '8px', color: '#9CA3AF' }}>{pos(t.saldo_credito)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>);
  },

  corrida_financiera: ({ content: c }) => {
    const corrida = c?.corrida;
    if (!corrida?.sections) return <p style={S.muted}>Sin datos de corrida disponibles.</p>;
    const m = c?.metrics_summary || {};
    const fv = (v, fmt) => { if (v == null) return ''; if (v === 0 && fmt === 'currency') return '—'; if (fmt === 'currency') return formatCurrency(v); if (fmt === 'pct') return `${formatNumber(v, 1)}%`; if (fmt === 'number') return formatNumber(v, 1); return v; };
    const secCol = { supuestos: '#4B5563', ingresos: '#6366F1', egresos: '#DC2626', resumen: '#0F172A' };
    return (<div>
      <p style={S.section}>Indicadores del Proyecto</p>
      <RenderKPIs metrics={m} highlight={['irr_annual', 'npv']} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '28px' }}>
        <div style={S.card}>
          <p style={S.label}>Ingresos Totales</p>
          <p style={{ ...S.bigNum, color: '#059669' }}>{formatCurrency(m.total_revenue)}</p>
        </div>
        <div style={S.card}>
          <p style={S.label}>Costo Total</p>
          <p style={{ ...S.bigNum, color: '#DC2626' }}>{formatCurrency(m.total_cost)}</p>
        </div>
        <div style={S.card}>
          <p style={S.label}>Utilidad Neta</p>
          <p style={{ ...S.bigNum, color: m.total_profit >= 0 ? '#059669' : '#DC2626' }}>{formatCurrency(m.total_profit)}</p>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '28px' }}>
        {[
          ['P.E. Ingresos', formatCurrency(m.breakeven_revenue)],
          ['P.E. Unidades', m.breakeven_units != null ? `${formatNumber(m.breakeven_units, 0)} uds` : 'N/A'],
          ['Utilidad / Unidad', formatCurrency(m.profit_per_unit)],
          ['Equity Aportado', formatCurrency(m.total_equity)],
          ['Intereses Pagados', formatCurrency(m.total_interest)],
        ].map(([label, value]) => (
          <div key={label} style={{ ...S.card, padding: '10px 12px' }}>
            <p style={{ ...S.label, fontSize: '8px' }}>{label}</p>
            <p style={{ ...S.medNum, fontSize: '13px' }}>{value}</p>
          </div>
        ))}
      </div>
      <p style={S.section}>Corrida Financiera</p>
      <div style={{ overflow: 'auto', maxHeight: '520px', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', minWidth: `${200 + (corrida.months || 0) * 85}px` }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '9px', fontWeight: 700, background: '#EEF2F7', position: 'sticky', left: 0, zIndex: 20, minWidth: '180px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9CA3AF' }}>Partida</th>
              {(corrida.month_labels || []).map((l, i) => <th key={`hdr-${l}-${i}`} style={{ textAlign: 'right', padding: '8px 6px', color: '#9CA3AF', background: '#EEF2F7', minWidth: '80px', fontSize: '9px', fontWeight: 600 }}>{l}</th>)}
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, background: '#EDE9E3', borderLeft: '2px solid #E5E7EB', minWidth: '90px', fontSize: '9px', textTransform: 'uppercase', color: '#4B5563' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {corrida.sections.map(section => {
              const col = secCol[section.id] || '#4B5563';
              return [
                <tr key={`s-${section.id}`}><td style={{ padding: '6px 12px', fontWeight: 700, color: 'white', background: col, position: 'sticky', left: 0, zIndex: 5, fontSize: '10px', letterSpacing: '0.5px' }}>{section.title}</td>{(corrida.month_labels || []).map((l, i) => <td key={`s-${section.id}-c-${l}-${i}`} style={{ background: col, padding: '6px' }} />)}<td style={{ background: col, padding: '6px' }} /></tr>,
                ...section.groups.flatMap((group, gi) => [
                  <tr key={`g-${section.id}-${gi}`}><td style={{ padding: '5px 12px', fontWeight: 700, fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: col, background: `${col}08`, position: 'sticky', left: 0, zIndex: 5 }}>{group.title}</td>{(corrida.month_labels || []).map((l, i) => <td key={`g-${section.id}-${gi}-c-${l}-${i}`} style={{ background: `${col}08`, padding: '5px' }} />)}<td style={{ background: `${col}08`, padding: '5px' }} /></tr>,
                  ...group.rows.map((row, ri) => {
                    const isTotal = row.is_total;
                    const isPO = row.key === 'res_utilidad' || row.key === 'res_pct_utilidad';
                    const isCum = row.key.includes('acum') || row.key.includes('saldo');
                    const vals = row.values || [];
                    const total = isPO ? (vals[vals.length - 1] || 0) : isCum ? (vals[vals.length - 1] || 0) : vals.reduce((s, v) => s + (v || 0), 0);
                    return (
                      <tr key={row.key} style={{ borderBottom: '1px solid #F0EDE8', background: isTotal ? `${col}06` : ri % 2 === 0 ? '#FAFAF8' : 'white' }}>
                        <td style={{ padding: '4px 12px', color: isTotal ? col : '#4B5563', fontWeight: isTotal ? 700 : 400, position: 'sticky', left: 0, zIndex: 5, background: isTotal ? `${col}06` : ri % 2 === 0 ? '#FAFAF8' : 'white', whiteSpace: 'nowrap' }}>{isTotal ? row.label : `  ${row.label}`}</td>
                        {vals.map((v, i) => <td key={`${row.key}-m${i}`} style={{ textAlign: 'right', padding: '4px 6px', fontFamily: 'monospace', color: v < 0 ? '#DC2626' : undefined, fontWeight: isTotal ? 600 : 400 }}>{isPO ? '' : fv(v, row.format)}</td>)}
                        <td style={{ textAlign: 'right', padding: '4px 12px', fontFamily: 'monospace', fontWeight: 700, borderLeft: '2px solid #E5E7EB', background: '#EEF2F7', color: total < 0 ? '#DC2626' : undefined }}>{fv(total, row.format)}</td>
                      </tr>
                    );
                  })
                ])
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>);
  },

  risk_alerts: ({ content: c }) => {
    const sevData = [
      { key: 'critical', label: 'Críticas', color: '#DC2626', bg: '#FDF2F2', border: '#E5AAAA' },
      { key: 'warning', label: 'Advertencias', color: '#D97706', bg: '#FFFBF5', border: '#E5CFA5' },
      { key: 'info', label: 'Informativas', color: '#6366F1', bg: '#F0FAFB', border: '#B5DDE2' },
    ];
    return (<div>
      <p style={S.section}>Resumen de Riesgos</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '28px' }}>
        {sevData.map(sv => (
          <div key={sv.key} style={{ border: `1px solid ${sv.border}`, borderRadius: '10px', padding: '16px', textAlign: 'center', background: sv.bg }}>
            <p style={{ fontSize: '28px', fontWeight: 700, color: sv.color, margin: '0 0 4px 0', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>{c?.[`${sv.key}_count`] || 0}</p>
            <p style={{ fontSize: '10px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0, fontWeight: 600 }}>{sv.label}</p>
          </div>
        ))}
      </div>
      <p style={S.section}>Detalle de Alertas</p>
      <RenderAlertsList alerts={c?.alerts} />
    </div>);
  },

  charts_summary: ({ content: c }) => {
    const rows = c?.rows || [];
    return (<div>
      <p style={S.section}>Datos de Gráficas</p>
      <div style={{ overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead><tr style={{ background: '#EEF2F7', borderBottom: '2px solid #E5E7EB' }}>
            {['Mes','Ingresos','Egresos','Flujo Neto','Acumulado','Crédito','Equity','Enganche','Parcial.','Escrit.','Terreno','Duro','Blando','Indirect.'].map(h =>
              <th key={h} style={{ textAlign: h === 'Mes' ? 'center' : 'right', padding: '6px', fontSize: '9px', color: '#9CA3AF', background: '#EEF2F7', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.3px' }}>{h}</th>
            )}
          </tr></thead>
          <tbody>{rows.map((r, i) =>
            <tr key={r.month} style={{ borderBottom: '1px solid #F0EDE8', background: i % 2 === 0 ? '#FAFAF8' : 'white' }}>
              <td style={{ textAlign: 'center', padding: '5px', fontWeight: 600 }}>{r.month}</td>
              {['ingresos','egresos','flujo_neto','flujo_acumulado','saldo_credito','equity_acum','enganche','parcialidades','escrituracion','terreno','costo_duro','costo_blando','indirectos'].map(k =>
                <td key={k} style={{ textAlign: 'right', padding: '5px', fontFamily: 'monospace', color: (k === 'flujo_neto' || k === 'flujo_acumulado') && r[k] < 0 ? '#DC2626' : (k === 'flujo_neto' || k === 'flujo_acumulado') && r[k] > 0 ? '#059669' : undefined }}>{r[k] > 0 || r[k] < 0 ? formatCurrency(r[k]) : '—'}</td>
              )}
            </tr>
          )}</tbody>
        </table>
      </div>
    </div>);
  },

  full_detailed: ({ content: c }) => {
    const ps = c?.project_summary || {};
    const ex = c?.executive || {};
    const cf = c?.cashflow || {};
    const ri = c?.risks || {};
    const exv = verdictView(ex.verdict);
    return (<div>
      {/* Executive diagnosis */}
      {ex.diagnosis_text && (<>
        <div style={{ padding: '16px 18px', borderRadius: '12px', border: `2px solid ${exv.border}`, background: exv.bg, marginBottom: '28px' }}>
          <p style={{ fontSize: '15px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', margin: '0 0 4px 0' }}>Diagnóstico: <span style={{ color: exv.text }}>{exv.label}</span></p>
          <p style={S.body}>{ex.diagnosis_text}</p>
        </div>
      </>)}
      {/* KPIs */}
      <p style={S.section}>Indicadores Clave</p>
      <RenderKPIs metrics={ps.kpis} highlight={['irr_annual', 'npv']} />
      {/* Summary */}
      <p style={S.section}>Resumen Financiero</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '28px' }}>
        <div style={S.card}><p style={S.label}>Ingresos</p><p style={{ ...S.bigNum, color: '#059669' }}>{formatCurrency(ps.summary?.total_revenue)}</p></div>
        <div style={S.card}><p style={S.label}>Costos</p><p style={{ ...S.bigNum, color: '#DC2626' }}>{formatCurrency(ps.summary?.total_cost)}</p></div>
        <div style={S.card}><p style={S.label}>Utilidad</p><p style={{ ...S.bigNum, color: ps.summary?.total_profit >= 0 ? '#059669' : '#DC2626' }}>{formatCurrency(ps.summary?.total_profit)}</p></div>
      </div>
      {/* Risks */}
      {(ri.alerts || []).filter(a => a.severity !== 'info').length > 0 && (<>
        <p style={S.section}>Riesgos Principales</p>
        <RenderAlertsList alerts={ri.alerts?.filter(a => a.severity !== 'info')} />
      </>)}
      {/* Recommendations */}
      {ex.recommendations?.length > 0 && (<>
        <p style={S.section}>Recomendaciones</p>
        {ex.recommendations.map((r, i) => (
          <div key={`ex-rec-${i}-${(r || '').slice(0, 24)}`} style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
            <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#6366F1', color: 'white', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
            <p style={{ ...S.body, margin: 0, paddingTop: '2px' }}>{r}</p>
          </div>
        ))}
      </>)}
      {/* Cashflow summary */}
      <p style={{ ...S.section, marginTop: '28px' }}>Flujo de Efectivo (resumen)</p>
      {RENDERERS.monthly_cashflow({ content: cf })}
    </div>);
  },
};


/* ═══════════════════════════ Main Page ═══════════════════════════ */

export default function ReportsPage() {
  const { activeProject, activeScenario } = useProject();
  const qc = useQueryClient();
  const { data: projects = [] } = useProjects();
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedScenario, setSelectedScenario] = useState('');
  const { data: scenarios = [] } = useScenarios(selectedProject);
  const { data: reports = [], isLoading: reportsLoading } = useReports(selectedProject || undefined);
  const generateMut = useGenerateReport();
  const deleteMut = useDeleteReport();
  const [reportTypes, setReportTypes] = useState([]);
  const [compareIds, setCompareIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewReport, setViewReport] = useState(null);
  // SWA-155 · marca y pie configurados en Ajustes
  const { data: brandSettings } = useSettings();
  const brandName = (brandSettings?.report_company_name || '').trim() || (brandSettings?.business_name || '').trim() || 'Stratika';
  const brandFooter = brandSettings?.report_footer_text || 'Documento confidencial preparado por el desarrollador. Cifras proyectadas, sujetas a cambio.';
  const brandContact = [brandSettings?.report_contact_email, brandSettings?.report_contact_phone].filter(x => x && x.trim()).join(' · ');
  const [filterType, setFilterType] = useState('all');
  const printRef = useRef(null);

  // Sync context
  if (activeProject?.id && !selectedProject) setSelectedProject(activeProject.id);
  if (activeScenario?.id && !selectedScenario && selectedProject) setSelectedScenario(activeScenario.id);

  // Load report types once
  useState(() => {
    reportsAPI.types().then(r => { setReportTypes(r.data); setLoading(false); }).catch(() => setLoading(false));
  });

  const handleGenerate = async (type) => {
    if (!selectedProject) { toast.error('Selecciona un proyecto'); return; }
    const meta = TYPES[type];
    if (meta?.needsScenario && !selectedScenario) { toast.error('Selecciona un escenario'); return; }
    if (meta?.needsCompare && compareIds.length < 2) { toast.error('Selecciona al menos 2 escenarios'); return; }
    try {
      const data = await generateMut.mutateAsync({ report_type: type, project_id: selectedProject, scenario_id: selectedScenario || null, scenario_ids: type === 'scenario_compare' ? compareIds : null });
      toast.success('Reporte generado');
      setViewReport(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleView = async (r) => {
    if (r.content) { setViewReport(r); return; }
    try { const res = await reportsAPI.get(r.id); setViewReport(res.data); } catch { toast.error('Error'); }
  };

  const handleDelete = async (id) => {
    try { await deleteMut.mutateAsync({ id, projectId: selectedProject }); toast.success('Eliminado'); if (viewReport?.id === id) setViewReport(null); } catch { toast.error('Error'); }
  };

  const handleRegenerate = async (r) => {
    try {
      const res = await reportsAPI.regenerate(r.id);
      toast.success('Reporte regenerado');
      qc.invalidateQueries({ queryKey: ['reports', selectedProject] });
      setViewReport(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al regenerar'); }
  };

  const handleExcel = async (r) => {
    try {
      toast.info('Generando Excel...');
      const response = await fetch(`/api/reports/${r.id}/excel`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) { toast.error('Error al generar Excel'); return; }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(r.report_label || r.report_type || 'reporte').replace(/\s/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Excel descargado');
    } catch (err) {
      toast.error('Error al descargar Excel');
    }
  };

  const handlePrint = () => {
    const el = printRef.current;
    if (!el) return;
    const isTable = viewReport?.report_type === 'corrida_financiera' || viewReport?.report_type === 'monthly_cashflow';
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>${viewReport?.report_label}</title>
      <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&family=Work+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        *{box-sizing:border-box;margin:0}
        body{font-family:'Plus Jakarta Sans',sans-serif;padding:20px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        table{page-break-inside:auto}
        tr{page-break-inside:avoid}
        @page{margin:10mm;size:${isTable ? 'A3 landscape' : 'A4 portrait'}}
        @media print{body{padding:0}[style*="maxHeight"],[style*="max-height"]{max-height:none!important;overflow:visible!important}
          .mcf-table{table-layout:auto}.mcf-table th,.mcf-table td{padding:3px 4px!important;font-size:7.5px!important;white-space:nowrap}}
      </style>
    </head><body>${el.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  const filteredReports = reports.filter(r => filterType === 'all' || r.report_type === filterType);
  const Renderer = viewReport ? RENDERERS[viewReport.report_type] : null;

  // Show guided state if no projects at all
  if (!loading && projects.length === 0 && !selectedProject) {
    return <RequiresProject moduleName="Reportes"><div /></RequiresProject>;
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="reports-page">
      <div className="px-6 lg:px-8 py-6 max-w-[1400px]">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>

          <div className="mb-8">
            <h1 className="font-display text-2xl font-semibold tracking-tight">Reportes</h1>
            <p className="text-sm text-muted-foreground mt-1">Genera y descarga reportes ejecutivos de tus proyectos</p>
          </div>

          <Tabs defaultValue="generate">
            <TabsList className="mb-6">
              <TabsTrigger value="generate">Generar Reporte</TabsTrigger>
              <TabsTrigger value="history">Historial ({reports.length})</TabsTrigger>
            </TabsList>

            {/* ═══ GENERATE TAB ═══ */}
            <TabsContent value="generate" className="space-y-6">
              {/* Context selectors */}
              <Card className="rounded-xl border-border/50 shadow-sm">
                <CardContent className="p-5">
                  <p className="text-xs font-display font-semibold text-muted-foreground uppercase tracking-wide mb-3">Contexto del reporte</p>
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="space-y-1.5">
                      <p className="text-xs text-muted-foreground">Proyecto</p>
                      <Select value={selectedProject} onValueChange={(v) => { setSelectedProject(v); setSelectedScenario(''); setCompareIds([]); }}>
                        <SelectTrigger className="w-[240px]"><SelectValue placeholder="Seleccionar proyecto" /></SelectTrigger>
                        <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-xs text-muted-foreground">Escenario</p>
                      <Select value={selectedScenario} onValueChange={setSelectedScenario}>
                        <SelectTrigger className="w-[240px]"><SelectValue placeholder="Seleccionar escenario" /></SelectTrigger>
                        <SelectContent>{scenarios.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Report type cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {Object.entries(TYPES).map(([id, t]) => {
                  const Icon = t.icon;
                  const isGen = generateMut.isPending;
                  const disabled = !selectedProject || (t.needsScenario && !selectedScenario) || (t.needsCompare && compareIds.length < 2) || generateMut.isPending;
                  return (
                    <Card key={id} className={`rounded-xl border-border/50 shadow-sm transition-all duration-200 ${disabled ? 'opacity-60' : 'hover:shadow-md hover:border-border'}`} data-testid={`report-type-${id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3.5 mb-4">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${t.color}12` }}>
                            <Icon className="h-5 w-5" style={{ color: t.color }} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-display font-semibold leading-tight">{t.label}</p>
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.desc}</p>
                          </div>
                        </div>
                        {t.needsCompare && (
                          <div className="mb-3">
                            <p className="text-[10px] text-muted-foreground font-medium mb-1.5">Escenarios a comparar:</p>
                            <div className="flex flex-wrap gap-1.5">
                              {scenarios.map(s => (
                                <button key={s.id} onClick={() => setCompareIds(prev => prev.includes(s.id) ? prev.filter(x => x !== s.id) : [...prev, s.id])}
                                  className={`text-[11px] px-2.5 py-1 rounded-lg border transition-[border-color,background] duration-150 ${compareIds.includes(s.id) ? 'border-[color:var(--primary-hex)] bg-accent font-medium' : 'border-border/50 text-muted-foreground hover:border-border'}`}>
                                  {s.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <Button className="w-full h-9 text-xs font-medium" disabled={disabled} onClick={() => handleGenerate(id)}
                          style={!disabled ? { background: t.color, color: 'white' } : {}}>
                          {isGen ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5 mr-1.5" />}
                          {isGen ? 'Generando...' : 'Generar reporte'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            {/* ═══ HISTORY TAB ═══ */}
            <TabsContent value="history" className="space-y-4">
              {/* Filter bar */}
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <div className="flex rounded-lg border border-border overflow-hidden">
                  {[['all', 'Todos'], ...Object.entries(TYPES).map(([k, v]) => [k, v.label])].map(([k, label]) => (
                    <button key={k} onClick={() => setFilterType(k)}
                      className={`px-3 py-1.5 text-xs font-medium transition-[background-color,color] duration-150 ${filterType === k ? 'text-white' : 'text-muted-foreground hover:bg-muted/50'}`}
                      style={filterType === k ? { background: 'var(--primary-hex)' } : {}}>
                      {label}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-muted-foreground ml-auto">{filteredReports.length} reportes</span>
              </div>

              {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
              ) : filteredReports.length === 0 ? (
                <Card className="rounded-xl border-border/50">
                  <CardContent className="py-16 text-center">
                    <FileText className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--primary-hex)', opacity: 0.2 }} />
                    <h3 className="font-display font-medium mb-1">{filterType === 'all' ? 'Sin reportes' : 'Sin reportes de este tipo'}</h3>
                    <p className="text-sm text-muted-foreground">Genera tu primer reporte desde la pestaña "Generar Reporte"</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {filteredReports.map((r, i) => {
                    const t = TYPES[r.report_type] || {};
                    const Icon = t.icon || FileText;
                    const color = t.color || '#4B5563';
                    return (
                      <motion.div key={r.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15, delay: i * 0.02 }}>
                        <Card className="rounded-xl border-border/50 shadow-sm hover:shadow-md transition-[box-shadow] duration-200 group" data-testid={`report-${r.id}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}12` }}>
                                <Icon className="h-4.5 w-4.5" style={{ color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-display font-semibold truncate">{r.report_label}</p>
                                  <Badge variant="outline" className="text-[9px] px-1.5 shrink-0">{r.project_name}</Badge>
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                                  {r.scenario_name && <span className="flex items-center gap-1"><ChevronRight className="h-3 w-3" />{r.scenario_name}</span>}
                                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(r.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                  {r.generated_by_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{r.generated_by_name}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0 opacity-50 group-hover:opacity-100 transition-opacity duration-150">
                                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => handleView(r)} data-testid={`view-${r.id}`}>
                                  <Eye className="h-3.5 w-3.5" /> Ver
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" title="Descargar Excel" onClick={() => handleExcel(r)}>
                                  <Download className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" title="Regenerar" onClick={() => handleRegenerate(r)} disabled={generateMut.isPending}>
                                  <RefreshCw className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(r.id)} title="Eliminar">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!viewReport} onOpenChange={(o) => { if (!o) setViewReport(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center justify-between">
              <span className="truncate">{viewReport?.report_label}</span>
              <div className="flex items-center gap-2 shrink-0 ml-4">
                <Button variant="outline" size="sm" onClick={() => handleExcel(viewReport)} className="text-xs gap-1.5">
                  <Download className="h-3.5 w-3.5" /> Excel
                </Button>
                <Button size="sm" onClick={handlePrint} style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90 text-xs gap-1.5">
                  <Printer className="h-3.5 w-3.5" /> PDF
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div ref={printRef} className="border border-border rounded-xl overflow-hidden">
            <div style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', color: '#0B1220', padding: '36px 32px', background: '#fff' }}>
              {/* Report header */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <p style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#6366F1', margin: '0 0 6px 0' }} data-testid="report-brand">{brandName}</p>
                    <h1 style={{ fontSize: '24px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', margin: '0 0 6px 0', color: '#0B1220' }}>{viewReport?.report_label}</h1>
                    <p style={{ fontSize: '14px', color: '#4B5563', margin: 0 }}>{viewReport?.project_name}{viewReport?.scenario_name ? ` — ${viewReport.scenario_name}` : ''}</p>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '11px', color: '#9A938A', lineHeight: 1.7 }}>
                    <p style={{ margin: 0 }}>{viewReport?.created_at ? new Date(viewReport.created_at).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
                    {viewReport?.generated_by_name && <p style={{ margin: 0 }}>Preparado por: {viewReport.generated_by_name}</p>}
                  </div>
                </div>
                <div style={{ height: '3px', background: 'linear-gradient(90deg, #6366F1 0%, #6366F1 60%, #E5E7EB 60%, #E5E7EB 100%)', borderRadius: '2px' }} />
              </div>
              {/* Report content */}
              {Renderer && viewReport && <Renderer content={viewReport.content} />}
              {/* Report footer */}
              <div style={{ marginTop: '40px', paddingTop: '14px', borderTop: '2px solid #E5E7EB' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontSize: '10px', fontWeight: 600, color: '#6366F1', margin: '0 0 2px 0', letterSpacing: '0.5px' }}>{brandName}</p>
                    <p style={{ fontSize: '9px', color: '#B5AFA8', margin: 0 }}>{brandContact || 'Preparado con Stratika'}</p>
                  </div>
                  <p style={{ fontSize: '9px', color: '#B5AFA8', margin: 0, fontStyle: 'italic' }} data-testid="report-footer">{brandFooter}</p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
